/**
 * Logger utility for consistent logging across the application
 * with support for different log levels and environments
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV || 'production';

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Determine the minimum log level for the current environment
 * local/development: debug (0)
 * demo/production: warn (2)
 */
const getMinLogLevel = (): number => {
  if (APP_ENV === 'prod' || APP_ENV === 'demo') {
    return LOG_LEVELS.warn;
  }
  return LOG_LEVELS.debug;
};

const minLogLevel = getMinLogLevel();

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= minLogLevel;
}

const formatMessage = (level: LogLevel, message: string, data?: any): string => {
  const timestamp = new Date().toISOString();
  // Don't stringify everything if we're in browser console as it's better to log objects directly
  return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
};

const logger = {
  debug: (message: string, data?: any) => {
    if (shouldLog('debug')) {
      console.debug(formatMessage('debug', message), data || '');
    }
  },

  info: (message: string, data?: any) => {
    if (shouldLog('info')) {
      console.info(formatMessage('info', message), data || '');
    }
  },

  warn: (message: string, data?: any) => {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn', message), data || '');
    }
  },

  error: (message: string, error?: any) => {
    if (shouldLog('error')) {
      console.error(formatMessage('error', message), error || '');
    }
  },

  // Performance specific logging
  performance: {
    logOperation: (id: string, phase: string, duration: number, metrics?: any) => {
      if (shouldLog('debug')) {
        console.info(
          `[PERFORMANCE] [${id}] ${phase} took ${duration.toFixed(2)}ms`,
          metrics || ''
        );
      }
    },
  },
};

export { logger };

